import React, { useState, useEffect } from 'react';
import './ChatWidget.css';
import { usersApiClient } from '../services/api';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

interface ChatWidgetProps {
  currentQuestion?: {
    question: string;
    correctAnswer: string;
    userAnswer: string;
    category: string;
  };
  isVisible?: boolean;
  onChatOpen?: () => void;
  onChatClose?: () => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ currentQuestion, isVisible = true, onChatOpen, onChatClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [pendingOpen, setPendingOpen] = useState(false);

  useEffect(() => {
    if (isOpen && messages.length === 0 && currentQuestion) {
      setMessages([{
        id: '1',
        text: "I see you got that question wrong. Would you like to understand why?",
        sender: 'ai',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, currentQuestion, messages.length]);

  // Reset chat when a new question arrives
  useEffect(() => {
    if (currentQuestion) {
      setMessages([]);
      setIsOpen(false);
      setInputText('');
      setPendingOpen(false);
    }
  }, [currentQuestion]);

  // Handle pending open state
  useEffect(() => {
    if (pendingOpen) {
      setIsOpen(true);
      setPendingOpen(false);
    }
  }, [pendingOpen]);


  const handleExplainWrongAnswer = async () => {
    if (!currentQuestion) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: "Why did I get it wrong?",
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // ChatGPT integration is now active!
    try {
      const response = await usersApiClient.post('/chat/explain-answer', {
        question: currentQuestion.question,
        correctAnswer: currentQuestion.correctAnswer,
        userAnswer: currentQuestion.userAnswer,
        category: currentQuestion.category
      });

      const data = response.data;

      const aiMessage: Message = {
        id: Date.now().toString(),
        text: data.explanation,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error getting explanation:', error);
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: "Sorry, I couldn't get an explanation right now. Please try again.",
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendFollowUp = async () => {
    const trimmed = inputText.trim();
    if (!trimmed || isLoading || !currentQuestion) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: trimmed,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Use the same endpoint but with follow-up context
      const response = await usersApiClient.post('/chat/explain-answer', {
        question: currentQuestion.question,
        correctAnswer: currentQuestion.correctAnswer,
        userAnswer: currentQuestion.userAnswer,
        category: currentQuestion.category,
        followUp: trimmed,
        conversationHistory: messages.map(m => ({
          role: m.sender === 'ai' ? 'assistant' : 'user',
          content: m.text
        }))
      });

      const data = response.data;

      const aiMessage: Message = {
        id: Date.now().toString(),
        text: data.explanation,
        sender: 'ai',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending follow-up:', error);
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: "Sorry, I couldn't process your follow-up question right now. Please try again.",
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Chat Button */}
      <button
        className={`chat-widget-button ${isOpen ? 'open' : ''}`}
        onClick={() => {
          if (!isOpen) {
            // Opening the chat
            setPendingOpen(true);
            if (onChatOpen) {
              onChatOpen();
            }
          } else {
            // Closing the chat
            setIsOpen(false);
            if (onChatClose) {
              onChatClose();
            }
          }
        }}
        aria-label="Open chat"
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        ) : (
          <img 
            src="https://res.cloudinary.com/dh5ugs2aa/image/upload/v1748397555/bible_ask_wsnadu.png"
            alt="Bible Study Assistant"
            className="chat-widget-icon"
          />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chat-widget-window">
          <div className="chat-widget-header">
            <h3>Bible Study Assistant</h3>
            <button 
              className="chat-widget-close"
              onClick={() => {
                setIsOpen(false);
                if (onChatClose) {
                  onChatClose();
                }
              }}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          <div className="chat-widget-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chat-message ${message.sender}`}
              >
                <div className="message-bubble">
                  {message.sender === 'ai' ? (
                    <div dangerouslySetInnerHTML={{ 
                      __html: message.text
                        .replace(/\n\n/g, '</p><p>')
                        .replace(/^/, '<p>')
                        .replace(/$/, '</p>')
                        .replace(/•/g, '&bull;')
                    }} />
                  ) : (
                    message.text
                  )}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="chat-message ai">
                <div className="message-bubble loading">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>

          {currentQuestion && messages.length === 1 && !isLoading && (
            <div className="chat-widget-actions">
              <button
                className="explain-button"
                onClick={handleExplainWrongAnswer}
              >
                Why did I get it wrong?
              </button>
            </div>
          )}

          {/* Input for follow-up questions */}
          {isOpen && messages.length > 1 && (
            <div className="chat-widget-actions">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSendFollowUp(); }}
                  placeholder="Ask a follow-up question..."
                  style={{
                    flex: 9,
                    padding: '12px 16px',
                    borderRadius: 25,
                    border: '1px solid #e5e7eb',
                    fontSize: '14px',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                  disabled={isLoading}
                />
                <button
                  className="send-arrow-button"
                  onClick={handleSendFollowUp}
                  disabled={isLoading || !inputText.trim()}
                  style={{
                    flex: 1,
                    maxWidth: '50px',
                    height: '44px',
                    padding: '0',
                    borderRadius: '50%',
                    border: 'none',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                    color: 'white',
                    cursor: isLoading || !inputText.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isLoading || !inputText.trim() ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  title="Send follow-up question"
                >
                  {isLoading ? (
                    <span style={{ fontSize: '12px' }}>⋯</span>
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;